import React, { useEffect } from 'react';
import { X, Mic, Sparkles, Download, Globe, ShieldCheck, Loader2, CheckCircle2 } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { requestMicrophonePermission } from '@/services/audio/micPermission';

/**
 * One-time onboarding for hands-free voice control. Shown when the user
 * flips the wake-word toggle on for the first time *in this browser* —
 * the IndexedDB model cache (`@xenova/transformers` with
 * `useBrowserCache: true`) is per-origin per-browser, so the
 * Whisper-tiny download happens once per browser per machine. The flag
 * lives in localStorage which is scoped the same way, so the seen-state
 * naturally tracks the cache scope.
 *
 * The modal also serves as the user's first introduction to the verb
 * grammar — wake word + start/pause/stop/resume — so they know what to
 * say once it's enabled. Source of truth for the verb list:
 * `src/workers/wakeword-grammar.ts`.
 */

const SEEN_KEY = 'maie.wakeword.introSeen';

export function hasSeenWakeWordOnboarding(): boolean {
  try {
    return localStorage.getItem(SEEN_KEY) === 'true';
  } catch {
    // Private browsing or quota exhausted — re-prompt every time. The
    // model itself is cached in IndexedDB which has its own quota, so a
    // re-prompted user just sees the modal again, not a re-download.
    return false;
  }
}

export function markWakeWordOnboardingSeen(): void {
  try {
    localStorage.setItem(SEEN_KEY, 'true');
  } catch {
    // Best-effort; silent failure is fine.
  }
}

/**
 * True when the wake-word Whisper model is *fully* present in the
 * @xenova/transformers Cache Storage on this browser.
 *
 * Why this exists: `enableWakeWord` may be true on a browser that has
 * never downloaded the model — either because the user cleared site data
 * (kept localStorage but lost the Cache Storage entry), or because the
 * setting traveled here through a future server-sync path, or because
 * the tab died mid-download last time. In any of those cases the wake
 * word silently won't work until the on-device download completes, so
 * we re-prompt rather than starting a hidden download.
 *
 * Critically we require the *load-bearing* files (encoder + decoder
 * ONNX, tokenizer, config) to ALL be present — a partial download
 * leaves a few small JSON files in the cache, and a "any whisper-tiny.en
 * file exists" probe would mistake that for a complete install.
 *
 * Returns false in any environment where Cache Storage isn't available
 * (e.g. some private modes, insecure contexts) — the caller treats
 * "unknown" as "needs prompting", which is the safe default.
 */
const TRANSFORMERS_CACHE_NAME = 'transformers-cache';
const WHISPER_MODEL_REPO = 'whisper-tiny.en';
const REQUIRED_FILE_FRAGMENTS = [
  'onnx/encoder_model_quantized.onnx',
  'onnx/decoder_model_merged_quantized.onnx',
  'tokenizer.json',
  'config.json',
];

export async function isWakeWordModelCached(): Promise<boolean> {
  try {
    if (typeof caches === 'undefined') return false;
    const has = await caches.has(TRANSFORMERS_CACHE_NAME);
    if (!has) return false;
    const cache = await caches.open(TRANSFORMERS_CACHE_NAME);
    const keys = await cache.keys();
    const whisperUrls = keys
      .map((req) => req.url)
      .filter((url) => url.includes(WHISPER_MODEL_REPO));
    return REQUIRED_FILE_FRAGMENTS.every((frag) =>
      whisperUrls.some((url) => url.endsWith(frag)),
    );
  } catch {
    return false;
  }
}

interface Props {
  isOpen: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  /**
   * Optional download-phase indicators. When the parent keeps the
   * modal open after `onConfirm` (so the doctor doesn't lose sight of
   * the model loading), pass these so the modal flips into a clear
   * "downloading… please wait" state and then a "ready" beat before
   * dismissing itself. Omit them and the modal behaves like the old
   * intro-only flow.
   */
  downloadProgress?: number | null;
  isDownloading?: boolean;
  isReady?: boolean;
  /** Called when the post-ready auto-dismiss timer fires. */
  onReadyDismiss?: () => void;
  /**
   * Close the modal but keep the download running in the background.
   * Different from `onCancel`, which stops everything. The corner chip
   * picks up progress reporting; a toast fires when the model is ready.
   */
  onMinimize?: () => void;
}

interface CommandRow {
  phrase: string;
  description: string;
}

export const WakeWordOnboardingModal: React.FC<Props> = ({
  isOpen,
  onCancel,
  onConfirm,
  downloadProgress,
  isDownloading,
  isReady,
  onReadyDismiss,
  onMinimize,
}) => {
  const { isRTL } = useTranslation();

  // Auto-dismiss the "Ready!" beat after a moment so the doctor
  // sees the success cue but isn't stuck clicking through. 1.4s is
  // long enough for the green check + label to register.
  useEffect(() => {
    if (!isOpen || !isReady || !onReadyDismiss) return;
    const id = setTimeout(onReadyDismiss, 1400);
    return () => clearTimeout(id);
  }, [isOpen, isReady, onReadyDismiss]);

  if (!isOpen) return null;

  const t = (en: string, ar: string) => (isRTL ? ar : en);
  const phase: 'intro' | 'downloading' | 'ready' = isReady
    ? 'ready'
    : isDownloading
      ? 'downloading'
      : 'intro';

  const commands: CommandRow[] = [
    {
      phrase: t('“Hey MAIE”', '«يا MAIE»'),
      description: t('Open the AI chat panel', 'فتح لوحة المحادثة مع MAIE'),
    },
    {
      phrase: t('“MAIE, start encounter”', '«MAIE، ابدأ الكشف»'),
      description: t(
        'Start recording an encounter (no patient required)',
        'بدء تسجيل كشف (دون الحاجة لاختيار مريض)',
      ),
    },
    {
      phrase: t('“MAIE, start next”', '«MAIE، ابدأ التالي»'),
      description: t(
        'Start the next scheduled appointment',
        'بدء الموعد المجدول التالي',
      ),
    },
    {
      phrase: t('“MAIE, pause”', '«MAIE، توقف مؤقتاً»'),
      description: t('Pause an active recording', 'إيقاف التسجيل مؤقتاً'),
    },
    {
      phrase: t('“MAIE, resume”', '«MAIE، تابع»'),
      description: t('Resume a paused recording', 'استئناف التسجيل المتوقف'),
    },
    {
      phrase: t('“MAIE, stop”', '«MAIE، أوقف»'),
      description: t('End the recording', 'إنهاء التسجيل'),
    },
  ];

  // Backdrop click on the intro phase cancels (no download started yet).
  // During download it minimizes to the corner chip — the user told us they
  // got stuck unable to dismiss the modal even though the copy promised
  // they could keep navigating, so backdrop should match that promise.
  // Falls back to `undefined` (modal stays open) when no minimize handler
  // is provided. The ready beat dismisses itself.
  const backdropOnClick =
    phase === 'intro'
      ? onCancel
      : phase === 'downloading'
        ? (onMinimize ?? undefined)
        : undefined;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'color-mix(in srgb, black 50%, transparent)' }}
      onClick={backdropOnClick}
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      <div
        className="relative w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden"
        style={{ backgroundColor: 'var(--color-bg-primary)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="px-6 py-5"
          style={{
            background:
              'linear-gradient(135deg, var(--color-primary-600), var(--color-accent))',
          }}
        >
          {(phase === 'intro' ||
            (phase === 'downloading' && onMinimize)) && (
            <button
              type="button"
              onClick={phase === 'intro' ? onCancel : onMinimize}
              className="absolute top-4 end-4 p-1.5 rounded-lg hover:bg-white/15 transition-colors"
              aria-label={
                phase === 'intro'
                  ? t('Close', 'إغلاق')
                  : t('Continue in background', 'المتابعة في الخلفية')
              }
              title={
                phase === 'downloading'
                  ? t('Continue in background', 'المتابعة في الخلفية')
                  : undefined
              }
            >
              <X className="w-5 h-5 text-white" />
            </button>
          )}
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-white/15">
              {phase === 'ready' ? (
                <CheckCircle2 className="w-6 h-6 text-white" />
              ) : phase === 'downloading' ? (
                <Loader2 className="w-6 h-6 text-white animate-spin" />
              ) : (
                <Mic className="w-6 h-6 text-white" />
              )}
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">
                {phase === 'ready'
                  ? t('Voice ready', 'الصوت جاهز')
                  : phase === 'downloading'
                    ? t('Downloading voice model…', 'جارٍ تنزيل النموذج الصوتي…')
                    : t('Hands-free MAIE', 'MAIE بدون استخدام اليدين')}
              </h2>
              <p className="text-sm text-white/85">
                {phase === 'ready'
                  ? t(
                      'You can say “Hey MAIE” now.',
                      'يمكنك قول «يا MAIE» الآن.',
                    )
                  : phase === 'downloading'
                    ? t(
                        'Please keep this tab open. This only happens once on this device.',
                        'الرجاء إبقاء هذه النافذة مفتوحة. يحدث هذا مرة واحدة فقط على هذا الجهاز.',
                      )
                    : t(
                        'Talk to MAIE — and run encounters — with your voice.',
                        'تحدث مع MAIE وأدر الكشف بصوتك.',
                      )}
              </p>
            </div>
          </div>
        </div>

        {phase === 'downloading' && (
          <div className="px-6 py-7 space-y-4">
            <div
              className="text-sm leading-relaxed"
              style={{ color: 'var(--color-text-secondary)' }}
            >
              {t(
                'A small voice model (~30 MB) is downloading to this browser. Voice commands will start working as soon as it finishes — usually under a minute on Wi-Fi.',
                'يتم تنزيل نموذج صوتي صغير (نحو 30 ميجابايت) إلى هذا المتصفح. ستعمل أوامر الصوت بمجرد اكتمال التنزيل — عادةً أقل من دقيقة عبر الواي فاي.',
              )}
            </div>
            <div className="space-y-2">
              <div
                className="flex items-center justify-between text-xs font-medium"
                style={{ color: 'var(--color-text-secondary)' }}
              >
                <span>{t('Downloading…', 'جارٍ التنزيل…')}</span>
                <span className="tabular-nums">
                  {downloadProgress != null
                    ? `${Math.round(downloadProgress)}%`
                    : t('starting…', 'جارٍ البدء…')}
                </span>
              </div>
              <div
                className="w-full h-2 rounded-full overflow-hidden"
                style={{ backgroundColor: 'var(--color-bg-secondary)' }}
                role="progressbar"
                aria-valuenow={downloadProgress ?? 0}
                aria-valuemin={0}
                aria-valuemax={100}
              >
                <div
                  className="h-full transition-[width] duration-200 ease-out"
                  style={{
                    width: `${Math.max(4, Math.min(100, downloadProgress ?? 0))}%`,
                    backgroundColor: 'var(--color-primary-600)',
                  }}
                />
              </div>
              <p
                className="text-[11px]"
                style={{ color: 'var(--color-text-tertiary)' }}
              >
                {t(
                  'You can keep using the rest of MAIE while this finishes — close this dialog and we’ll let you know when it’s ready.',
                  'يمكنك الاستمرار في استخدام بقية تطبيق MAIE حتى يكتمل التنزيل — أغلق هذه النافذة وسنُعلمك عند الجاهزية.',
                )}
              </p>
            </div>
            {onMinimize && (
              <button
                type="button"
                onClick={onMinimize}
                className="w-full px-4 py-2.5 rounded-xl text-sm font-medium transition-colors"
                style={{
                  border: '1px solid var(--color-border-primary)',
                  color: 'var(--color-text-primary)',
                  backgroundColor: 'var(--color-bg-primary)',
                }}
              >
                {t('Continue in background', 'المتابعة في الخلفية')}
              </button>
            )}
          </div>
        )}

        {phase === 'ready' && (
          <div className="px-6 py-7 flex items-center gap-3">
            <CheckCircle2
              className="w-5 h-5 flex-shrink-0"
              style={{ color: 'var(--color-primary-600)' }}
            />
            <div
              className="text-sm leading-relaxed"
              style={{ color: 'var(--color-text-primary)' }}
            >
              {t(
                'All set. Try saying “Hey MAIE, start next”.',
                'تم الإعداد. جرّب قول «يا MAIE، ابدأ التالي».',
              )}
            </div>
          </div>
        )}

        {phase === 'intro' && (
        <>
        {/* Body */}
        <div className="px-6 py-5 space-y-5 max-h-[70vh] overflow-y-auto">
          {/* Command grammar */}
          <section>
            <div className="flex items-center gap-2 mb-2">
              <Sparkles
                className="w-4 h-4"
                style={{ color: 'var(--color-primary-600)' }}
              />
              <h3
                className="text-sm font-semibold tracking-wide uppercase"
                style={{ color: 'var(--color-text-secondary)' }}
              >
                {t('What you can say', 'ما يمكنك قوله')}
              </h3>
            </div>
            <ul className="space-y-2">
              {commands.map((cmd) => (
                <li
                  key={cmd.phrase}
                  className="flex items-start gap-3 p-3 rounded-lg"
                  style={{
                    backgroundColor: 'var(--color-bg-secondary)',
                    border: '1px solid var(--color-border-primary)',
                  }}
                >
                  <code
                    className="text-sm font-semibold whitespace-nowrap"
                    style={{ color: 'var(--color-primary-600)' }}
                  >
                    {cmd.phrase}
                  </code>
                  <span
                    className="text-sm flex-1"
                    style={{ color: 'var(--color-text-secondary)' }}
                  >
                    {cmd.description}
                  </span>
                </li>
              ))}
            </ul>
            <p
              className="text-xs mt-2"
              style={{ color: 'var(--color-text-secondary)' }}
            >
              {t(
                'Wake word required for every command — “stop” by itself never fires.',
                'كلمة التنبيه مطلوبة لكل أمر — لن يعمل «أوقف» وحدها.',
              )}
            </p>
          </section>

          {/* Setup notes */}
          <section className="space-y-3">
            <div className="flex items-center gap-2">
              <Download
                className="w-4 h-4"
                style={{ color: 'var(--color-primary-600)' }}
              />
              <h3
                className="text-sm font-semibold tracking-wide uppercase"
                style={{ color: 'var(--color-text-secondary)' }}
              >
                {t('One-time setup', 'إعداد لمرة واحدة')}
              </h3>
            </div>
            <ul className="space-y-2.5">
              <li
                className="flex items-start gap-3 text-sm"
                style={{ color: 'var(--color-text-primary)' }}
              >
                <Download
                  className="w-4 h-4 mt-0.5 flex-shrink-0"
                  style={{ color: 'var(--color-text-secondary)' }}
                />
                <span>
                  {t(
                    'Downloads a small voice model to this browser the first time you turn it on. After that, it loads instantly.',
                    'يقوم بتنزيل نموذج صوتي صغير إلى هذا المتصفح عند التفعيل لأول مرة. بعد ذلك، يتم التحميل فوراً.',
                  )}
                </span>
              </li>
              <li
                className="flex items-start gap-3 text-sm"
                style={{ color: 'var(--color-text-primary)' }}
              >
                <Globe
                  className="w-4 h-4 mt-0.5 flex-shrink-0"
                  style={{ color: 'var(--color-text-secondary)' }}
                />
                <span>
                  {t(
                    'Cached per browser. Using MAIE on a different browser or device means another download there.',
                    'يتم التخزين لكل متصفح على حدة. استخدام MAIE على متصفح أو جهاز آخر يعني تنزيلاً آخر.',
                  )}
                </span>
              </li>
              <li
                className="flex items-start gap-3 text-sm"
                style={{ color: 'var(--color-text-primary)' }}
              >
                <ShieldCheck
                  className="w-4 h-4 mt-0.5 flex-shrink-0"
                  style={{ color: 'var(--color-text-secondary)' }}
                />
                <span>
                  {t(
                    'Wake-word listening runs locally in your browser — no audio leaves your device until you start an encounter.',
                    'يعمل اكتشاف كلمة التنبيه محلياً داخل المتصفح — لا يغادر أي صوت جهازك حتى تبدأ كشفاً.',
                  )}
                </span>
              </li>
            </ul>
          </section>
        </div>

        {/* Actions */}
        <div
          className="px-6 py-4 flex gap-3 border-t"
          style={{
            backgroundColor: 'var(--color-bg-secondary)',
            borderColor: 'var(--color-border-primary)',
          }}
        >
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors"
            style={{
              border: '1px solid var(--color-border-primary)',
              color: 'var(--color-text-primary)',
              backgroundColor: 'var(--color-bg-primary)',
            }}
          >
            {t('Not now', 'ليس الآن')}
          </button>
          <button
            type="button"
            onClick={async () => {
              // Surface the OS mic prompt while the user is still inside
              // a clear "yes I want this" gesture. Without this the wake
              // word worker calls getUserMedia later and silently fails
              // — a Web Worker's permission request can't bubble up to
              // a system dialog on iOS, and on Android the WebView's
              // bridge needs RECORD_AUDIO already granted at the app
              // level for the worker to proceed.
              const status = await requestMicrophonePermission();
              markWakeWordOnboardingSeen();
              if (status === 'granted' || status === 'unavailable') {
                onConfirm();
              } else {
                // Denied or still 'prompt' (user dismissed without
                // choosing). Treat as cancel — let the caller's UI
                // show the standard "enable in settings" path on the
                // next attempt instead of starting a worker that will
                // immediately error.
                onCancel();
              }
            }}
            className="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold text-white shadow-md transition-transform hover:scale-[1.01] active:scale-95"
            style={{
              background:
                'linear-gradient(135deg, var(--color-primary-600), var(--color-accent))',
            }}
          >
            {t('Enable & download', 'تفعيل والتنزيل')}
          </button>
        </div>
        </>
        )}
      </div>
    </div>
  );
};

export default WakeWordOnboardingModal;
